import { useRef, useState } from "react";
import { useLocation } from "react-router";
import { ArrowLeft, ChevronRight, Home, Loader2, Search, UploadCloud } from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { useConfirm } from "@/lib/confirm";
import { useSetPageHeader } from "@/lib/page-header";
import { FilesUploadButton } from "@/components/files/FilesUploadButton";
import { useFileUpload } from "@/components/files/use-file-upload";
import { FilesToolbar } from "@/components/files/FilesToolbar";
import { FilesGrid } from "@/components/files/FilesGrid";
import { FilesTable } from "@/components/files/FilesTable";
import { EntityRootList } from "@/components/files/EntityRootList";
import { EntityFilesView } from "@/components/files/EntityFilesView";
import { CreateFolderDialog } from "@/components/files/CreateFolderDialog";
import { RenameDialog } from "@/components/files/RenameDialog";
import { MoveDialog, type MoveTarget } from "@/components/files/MoveDialog";
import { FileTagsDialog } from "@/components/files/FileTagsDialog";
import { FileSearchDialog } from "@/components/files/FileSearchDialog";
import { FilePreviewDialog } from "@/components/files/FilePreviewDialog";
import { ShareDialog } from "@/components/files/ShareDialog";
import { FileHistoryDialog } from "@/components/files/FileHistoryDialog";
import {
  compareFiles,
  FIXED_ENTITY_FOLDERS,
  type BrowseMode,
  type DraggedItem,
  type FileRow,
  type FolderRow,
  type SortDirection,
  type SortField,
} from "@/components/files/files-types";
import { useTourDaPagina } from "@/components/product-tour/PageTourEntry";
import { getArquivosTourSteps } from "@/lib/product-tour/page-tour-steps";

export function Arquivos() {
  const location = useLocation();

  useTourDaPagina(getArquivosTourSteps(location.pathname));
  useSetPageHeader(
    {
      title: "Arquivos",
      subtitle: "Todos os arquivos organizados automaticamente por entidade",
    },
    [location.pathname],
  );

  const utils = trpc.useUtils();
  const confirm = useConfirm();

  const [mode, setMode] = useState<BrowseMode>({ kind: "folder", folderId: null });
  const [viewMode, setViewMode] = useState<"grade" | "lista">("grade");
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState<SortField>("name");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState<{ id: string; name: string; kind: "arquivo" | "pasta" } | null>(null);
  const [moveTarget, setMoveTarget] = useState<MoveTarget>(null);
  const [tagsTargetId, setTagsTargetId] = useState<string | null>(null);
  const [shareTargetId, setShareTargetId] = useState<string | null>(null);
  const [historyTargetId, setHistoryTargetId] = useState<string | null>(null);
  const [previewFile, setPreviewFile] = useState<FileRow | null>(null);

  const folderId = mode.kind === "folder" ? mode.folderId : null;
  const folderListing = trpc.file.listFolder.useQuery({ folderId }, { enabled: mode.kind === "folder" });

  const invalidateAll = () => utils.file.invalidate();

  const createFolder = trpc.file.createFolder.useMutation({
    onSuccess: () => {
      toast.success("Pasta criada.");
      setCreateFolderOpen(false);
      invalidateAll();
    },
    onError: (e) => toast.error(e.message || "Erro ao criar pasta."),
  });
  const renameFolder = trpc.file.renameFolder.useMutation({
    onSuccess: () => {
      toast.success("Pasta renomeada.");
      setRenameTarget(null);
      invalidateAll();
    },
    onError: (e) => toast.error(e.message || "Erro ao renomear pasta."),
  });
  const moveFolder = trpc.file.moveFolder.useMutation({
    onSuccess: () => {
      toast.success("Pasta movida.");
      setMoveTarget(null);
      invalidateAll();
    },
    onError: (e) => toast.error(e.message || "Erro ao mover pasta."),
  });
  const deleteFolder = trpc.file.deleteFolder.useMutation({
    onSuccess: () => {
      toast.success("Pasta excluída.");
      invalidateAll();
    },
    onError: (e) => toast.error(e.message || "Erro ao excluir pasta."),
  });

  const renameFile = trpc.file.rename.useMutation({
    onSuccess: () => {
      toast.success("Arquivo renomeado.");
      setRenameTarget(null);
      invalidateAll();
    },
    onError: (e) => toast.error(e.message || "Erro ao renomear arquivo."),
  });
  const moveFile = trpc.file.move.useMutation({
    onSuccess: () => {
      toast.success("Arquivo movido.");
      setMoveTarget(null);
      invalidateAll();
    },
    onError: (e) => toast.error(e.message || "Erro ao mover arquivo."),
  });
  const deleteFile = trpc.file.delete.useMutation({
    onSuccess: () => {
      toast.success("Arquivo excluído.");
      invalidateAll();
    },
    onError: (e) => toast.error(e.message || "Erro ao excluir arquivo."),
  });
  const attachToEntity = trpc.file.attachToEntity.useMutation({ onSuccess: invalidateAll });

  function goUp() {
    if (mode.kind !== "folder" || mode.folderId === null) return;
    const bc = folderListing.data?.breadcrumb ?? [];
    const parentId = bc.length >= 2 ? bc[bc.length - 2].id : null;
    setMode({ kind: "folder", folderId: parentId });
  }

  function handleUploaded(file: { fileId: string; name: string }) {
    invalidateAll();
    if (mode.kind === "entity") {
      attachToEntity.mutate({ fileId: file.fileId, relatedType: mode.relatedType, relatedId: mode.relatedId });
    } else if (mode.kind === "folder" && mode.folderId) {
      // já sobe na pasta certa: requestUpload não sabe de pasta, então move logo em seguida.
      moveFile.mutate({ fileId: file.fileId, folderId: mode.folderId });
    }
  }

  // Arraste de arquivo(s) do sistema operacional direto em cima de uma pasta específica — sobe
  // já dentro dela, mesmo que não seja a pasta atualmente aberta.
  const dropTargetFolderIdRef = useRef<string | null>(null);
  const folderDropUpload = useFileUpload({
    kind: "GENERIC",
    onUploaded: (file) => {
      invalidateAll();
      const targetId = dropTargetFolderIdRef.current;
      if (targetId) moveFile.mutate({ fileId: file.fileId, folderId: targetId });
    },
  });
  function handleDropFilesOnFolder(folder: FolderRow, files: FileList) {
    dropTargetFolderIdRef.current = folder.id;
    void folderDropUpload.handleFiles(files);
  }

  // Soltou um item (arquivo/pasta) arrastado em cima de outra pasta — move.
  function handleDropItemOnFolder(item: DraggedItem, folder: FolderRow) {
    if (item.kind === "arquivo") moveFile.mutate({ fileId: item.id, folderId: folder.id });
    else moveFolder.mutate({ folderId: item.id, parentId: folder.id });
  }

  // Arraste de arquivo(s) do SO soltos na área vazia da pasta atual (fora de qualquer pasta
  // específica) — sobe na pasta que está aberta agora. Área inteira reage, não só uma caixa.
  const [areaDragOver, setAreaDragOver] = useState(false);
  const currentFolderUpload = useFileUpload({ kind: "GENERIC", onUploaded: handleUploaded });
  function handleAreaDragOver(e: React.DragEvent) {
    if (!e.dataTransfer.types.includes("Files")) return;
    e.preventDefault();
    setAreaDragOver(true);
  }
  function handleAreaDrop(e: React.DragEvent) {
    if (!e.dataTransfer.types.includes("Files")) return;
    e.preventDefault();
    setAreaDragOver(false);
    if (e.dataTransfer.files.length > 0) void currentFolderUpload.handleFiles(e.dataTransfer.files);
  }

  async function handleDownload(file: FileRow) {
    try {
      const { url } = await utils.client.file.getDownloadUrl.query({ fileId: file.id });
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      toast.error("Não foi possível baixar o arquivo.");
    }
  }

  async function handleDeleteFile(file: FileRow) {
    const ok = await confirm({
      title: "Excluir arquivo?",
      description: `"${file.name}" será excluído permanentemente. Essa ação não tem volta.`,
      confirmText: "Excluir",
      destructive: true,
    });
    if (ok) deleteFile.mutate({ fileId: file.id });
  }

  async function handleDeleteFolder(folder: FolderRow) {
    const ok = await confirm({
      title: "Excluir pasta?",
      description: `"${folder.name}" e subpastas serão excluídas. Arquivos dentro dela voltam pra raiz — nenhum arquivo é apagado.`,
      confirmText: "Excluir pasta",
      destructive: true,
    });
    if (ok) deleteFolder.mutate({ folderId: folder.id });
  }

  function handleMoveConfirm(targetFolderId: string | null) {
    if (!moveTarget) return;
    if (moveTarget.kind === "arquivo") moveFile.mutate({ fileId: moveTarget.id, folderId: targetFolderId });
    else moveFolder.mutate({ folderId: moveTarget.id, parentId: targetFolderId });
  }

  function handleRenameConfirm(name: string) {
    if (!renameTarget) return;
    if (renameTarget.kind === "arquivo") renameFile.mutate({ fileId: renameTarget.id, name });
    else renameFolder.mutate({ folderId: renameTarget.id, name });
  }

  const fixedEntries =
    mode.kind === "folder" && mode.folderId === null
      ? FIXED_ENTITY_FOLDERS.map((entry) => ({
          key: entry.relatedType,
          label: entry.label,
          icon: entry.icon,
          onClick: () => setMode({ kind: "entityRoot", relatedType: entry.relatedType, label: entry.label }),
        }))
      : undefined;

  const term = search.trim().toLowerCase();
  const visibleFolders = ((folderListing.data?.subfolders ?? []) as FolderRow[])
    .filter((f) => !term || f.name.toLowerCase().includes(term))
    .sort((a, b) => a.name.localeCompare(b.name, "pt-BR"));
  const visibleFiles = ((folderListing.data?.files ?? []) as FileRow[])
    .filter((f) => !term || f.name.toLowerCase().includes(term))
    .sort((a, b) => compareFiles(a, b, sortField, sortDirection));

  const fileActions = {
    onOpenFolder: (f: FolderRow) => setMode({ kind: "folder", folderId: f.id }),
    onOpenFile: setPreviewFile,
    onRenameFolder: (f: FolderRow) => setRenameTarget({ id: f.id, name: f.name, kind: "pasta" }),
    onMoveFolder: (f: FolderRow) => setMoveTarget({ id: f.id, name: f.name, kind: "pasta" }),
    onDeleteFolder: handleDeleteFolder,
    onDownloadFile: handleDownload,
    onTagsFile: (f: FileRow) => setTagsTargetId(f.id),
    onShareFile: (f: FileRow) => setShareTargetId(f.id),
    onHistoryFile: (f: FileRow) => setHistoryTargetId(f.id),
    onRenameFile: (f: FileRow) => setRenameTarget({ id: f.id, name: f.name, kind: "arquivo" }),
    onMoveFile: (f: FileRow) => setMoveTarget({ id: f.id, name: f.name, kind: "arquivo" }),
    onDeleteFile: handleDeleteFile,
    onDropItemOnFolder: handleDropItemOnFolder,
    onDropFilesOnFolder: handleDropFilesOnFolder,
  };

  return (
    <div className="mx-auto w-full max-w-[1600px]">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
      <div className="flex flex-wrap items-center gap-1.5 text-sm text-zinc-500 dark:text-muted-foreground">
        {mode.kind === "folder" && (
          <button
            type="button"
            onClick={goUp}
            disabled={mode.folderId === null}
            aria-label="Voltar para o diretório anterior"
            title="Voltar para o diretório anterior"
            className="mr-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-900 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent dark:text-muted-foreground dark:hover:bg-muted dark:hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
        )}
        <button
          type="button"
          onClick={() => setMode({ kind: "folder", folderId: null })}
          className="flex items-center gap-1 font-medium hover:text-brand-700 hover:underline dark:hover:text-primary"
        >
          <Home className="h-3.5 w-3.5" />
          Arquivos
        </button>
        {mode.kind === "folder" &&
          folderListing.data?.breadcrumb.map((b: { id: string; name: string }) => (
            <span key={b.id} className="flex items-center gap-1.5">
              <ChevronRight className="h-3.5 w-3.5" />
              <button
                type="button"
                onClick={() => setMode({ kind: "folder", folderId: b.id })}
                className="font-medium hover:text-brand-700 hover:underline dark:hover:text-primary"
              >
                {b.name}
              </button>
            </span>
          ))}
        {mode.kind === "entityRoot" && (
          <span className="flex items-center gap-1.5">
            <ChevronRight className="h-3.5 w-3.5" />
            <span className="font-semibold text-zinc-900 dark:text-foreground">{mode.label}</span>
          </span>
        )}
        {mode.kind === "entity" && (
          <>
            <span className="flex items-center gap-1.5">
              <ChevronRight className="h-3.5 w-3.5" />
              <button
                type="button"
                onClick={() =>
                  setMode({
                    kind: "entityRoot",
                    relatedType: mode.relatedType,
                    label: FIXED_ENTITY_FOLDERS.find((e) => e.relatedType === mode.relatedType)?.label ?? "",
                  })
                }
                className="font-medium hover:text-brand-700 hover:underline dark:hover:text-primary"
              >
                {FIXED_ENTITY_FOLDERS.find((e) => e.relatedType === mode.relatedType)?.label}
              </button>
            </span>
            <span className="flex items-center gap-1.5">
              <ChevronRight className="h-3.5 w-3.5" />
              <span className="font-semibold text-zinc-900 dark:text-foreground">{mode.label}</span>
            </span>
          </>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {mode.kind !== "entityRoot" && (
          <FilesUploadButton kind="GENERIC" onUploaded={handleUploaded} />
        )}
        <button
          type="button"
          data-tour="arquivos-busca-global"
          onClick={() => setSearchOpen(true)}
          className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-3 text-xs font-medium text-zinc-600 hover:bg-zinc-50 dark:border-border dark:bg-card dark:text-muted-foreground dark:hover:bg-muted"
        >
          <Search className="h-3.5 w-3.5" />
          Busca global
        </button>
      </div>
      </div>

      {mode.kind === "folder" && (
        <>
          <FilesToolbar
            search={search}
            onSearchChange={setSearch}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            sortField={sortField}
            onSortFieldChange={setSortField}
            sortDirection={sortDirection}
            onToggleSortDirection={() => setSortDirection((d) => (d === "asc" ? "desc" : "asc"))}
            onNewFolder={() => setCreateFolderOpen(true)}
          />
          <div
            onDragOver={handleAreaDragOver}
            onDragLeave={(e) => {
              if (e.currentTarget === e.target) setAreaDragOver(false);
            }}
            onDrop={handleAreaDrop}
            className="relative"
          >
            {areaDragOver && (
              <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-lg border-2 border-dashed border-brand-400 bg-brand-50/90 dark:border-primary/60 dark:bg-primary/10">
                <p className="flex items-center gap-2 text-sm font-semibold text-brand-700 dark:text-primary">
                  <UploadCloud className="h-5 w-5" />
                  Solte para enviar nesta pasta
                </p>
              </div>
            )}
            {folderListing.isLoading ? (
              <div className="flex justify-center py-16">
                <Loader2 className="h-6 w-6 animate-spin text-brand-600" />
              </div>
            ) : viewMode === "grade" ? (
              <FilesGrid fixedEntries={fixedEntries} folders={visibleFolders} files={visibleFiles} actions={fileActions} />
            ) : (
              <FilesTable fixedEntries={fixedEntries} folders={visibleFolders} files={visibleFiles} actions={fileActions} />
            )}
          </div>
        </>
      )}

      {mode.kind === "entityRoot" && (
        <EntityRootList
          relatedType={mode.relatedType}
          onOpenEntity={(relatedId, label) => setMode({ kind: "entity", relatedType: mode.relatedType, relatedId, label })}
        />
      )}

      {mode.kind === "entity" && (
        <EntityFilesView
          relatedType={mode.relatedType}
          relatedId={mode.relatedId}
          viewMode={viewMode}
          onOpenFile={setPreviewFile}
          onDownloadFile={handleDownload}
          onTagsFile={(f) => setTagsTargetId(f.id)}
          onShareFile={(f) => setShareTargetId(f.id)}
          onHistoryFile={(f) => setHistoryTargetId(f.id)}
          onRenameFile={(f) => setRenameTarget({ id: f.id, name: f.name, kind: "arquivo" })}
          onMoveFile={(f) => setMoveTarget({ id: f.id, name: f.name, kind: "arquivo" })}
          onDeleteFile={handleDeleteFile}
        />
      )}

      <CreateFolderDialog
        open={createFolderOpen}
        onClose={() => setCreateFolderOpen(false)}
        onCreate={(name) => createFolder.mutate({ name, parentId: folderId })}
        isPending={createFolder.isPending}
      />
      <RenameDialog
        target={renameTarget}
        onClose={() => setRenameTarget(null)}
        onRename={handleRenameConfirm}
        isPending={renameFile.isPending || renameFolder.isPending}
      />
      <MoveDialog
        target={moveTarget}
        onClose={() => setMoveTarget(null)}
        onMove={handleMoveConfirm}
        isPending={moveFile.isPending || moveFolder.isPending}
      />
      <FileTagsDialog fileId={tagsTargetId} onClose={() => setTagsTargetId(null)} />
      <ShareDialog fileId={shareTargetId} onClose={() => setShareTargetId(null)} />
      <FileHistoryDialog fileId={historyTargetId} onClose={() => setHistoryTargetId(null)} />
      <FilePreviewDialog file={previewFile} onClose={() => setPreviewFile(null)} />
      <FileSearchDialog open={searchOpen} onClose={() => setSearchOpen(false)} onDownload={handleDownload} />
    </div>
  );
}
